import { gql } from "@apollo/client";

import {
  CANDIDATE_FRAGMENT,
  CANDIDATE_URLS_FRAGMENT,
} from "./jobs";

export const INVITE_RECRUITER_URL_MUTATION = gql`
  mutation InviteRecruiterContactUrlMutation(
    $firstName: String!
    $lastName: String!
    $email: String!
  ) {
    inviteRecruiterContactUrl(
      firstName: $firstName
      lastName: $lastName
      email: $email
    )
  }
`;

export const GET_RECRUITER_USERS_QUERY = gql`
  query GetRecruiterUsersQuery {
    getRecruiterUsers {
      id
      fullName
      firstName
      lastName
      email
      isOwner
    }
  }
`;

export const GET_CANDIDATE_USER_QUERY = gql`
  ${CANDIDATE_FRAGMENT}
  ${CANDIDATE_URLS_FRAGMENT}
  query Candidate($candidateProfileId: ID!) {
    candidate(candidateProfileId: $candidateProfileId) {
      ...CandidateFragment
      ...CandidateUrlsFragment
      favourite
      candidateProfile {
        id
        roles {
          id
          name
        }
      }
    }
  }
`;

// Legacy deep-link fallback: older candidate-detail links carry the candidate's
// user id (the route is now keyed on candidateProfileId). Resolves a user id to
// its candidate profile id so we can redirect to the canonical URL.
export const GET_CANDIDATE_PROFILE_ID_FOR_USER_QUERY = gql`
  query GetCandidateProfileIdForUser($id: String!) {
    getUser(id: $id) {
      id
      candidateProfile {
        id
      }
    }
  }
`;

export const AGENCIES_QUERY = gql`
  query Agencies {
    agencies {
      id
      name
      type
      logoUrl
      slugId
      disabledAt
    }
  }
`;

export const CREATE_AGENCY_MUTATION = gql`
  mutation CreateAgencyMutation($name: String!, $agencyType: String!, $logo: Upload!) {
    createAgency(name: $name, agencyType: $agencyType, logo: $logo) {
      id
      name
      type
      logoUrl
      slugId
    }
  }
`;

export const UPDATE_AGENCY_MUTATION = gql`
  mutation UpdateAgencyMutation($slugId: String!, $name: String!, $agencyType: String!, $logo: Upload) {
    updateAgency(slugId: $slugId, name: $name, agencyType: $agencyType, logo: $logo) {
      id
      name
      type
      logoUrl
      slugId
    }
  }
`;

export const UPDATE_AGENCY_STATUS_MUTATION = gql`
  mutation UpdateAgencyStatusMutation($slugId: String!, $status: String!) {
    updateAgencyStatus(slugId: $slugId, status: $status) {
      id
      disabledAt
      slugId
    }
  }
`;

export const UPDATE_CANDIDATE_PROFILE_MUTATION = gql`
  mutation UpdateCandidateProfileMutation(
    $candidateProfileId: ID!
    $firstName: String!
    $lastName: String!
    $email: String!
    $primaryPhone: String!
  ) {
    updateCandidateProfile(
      candidateProfileId: $candidateProfileId
      firstName: $firstName
      lastName: $lastName
      email: $email
      primaryPhone: $primaryPhone
    ) {
      candidateProfileId
    }
  }
`;

export const UPDATE_CANDIDATE_ROLE_PREF_MUTATION = gql`
  mutation UpdateCandidateRolePrefMutation(
    $candidateProfileId: ID!
    $roleIds: [ID!]
    $skillIds: [SkillIdsInput]
  ) {
    updateCandidateRolePref(
      candidateProfileId: $candidateProfileId
      roleIds: $roleIds
      skillIds: $skillIds
    ) {
      candidateProfileId
    }
  }
`;

export const UPLOAD_CANDIDATE_RESUME_MUTATION = gql`
  mutation UploadCandidateResumeMutation(
    $candidateProfileId: ID!
    $resumeFile: Upload!
  ) {
    uploadCandidateResume(
      candidateProfileId: $candidateProfileId
      resumeFile: $resumeFile
    ) {
      candidateProfileId
    }
  }
`;

export const INVITE_CANDIDATE_MUTATION = gql`
  mutation InviteCandidateMutation(
    $bullhornId: String
    $firstName: String!
    $lastName: String!
    $email: String!
    $phone: String!
    $certification: [CertificationInput!]
  ) {
    inviteCandidate(
      bullhornId: $bullhornId
      firstName: $firstName
      lastName: $lastName
      email: $email
      phone: $phone
      certification: $certification
    ) {
      id
      certification {
        id
        certificationId
        name
      }
    }
  }
`;

export const UPLOAD_EXTERNAL_CANDIDATE_RESUME_MUTATION = gql`
  mutation UploadExternalCandidateResumeMutation(
    $externalCandidateId: ID!
    $resumeFile: Upload!
  ) {
    uploadExternalCandidateResume(
      externalCandidateId: $externalCandidateId
      resumeFile: $resumeFile
    )
  }
`;

export const UPLOAD_EXTERNAL_CANDIDATE_CERTIFICATION_MUTATION = gql`
  mutation UploadExternalCandidateCertificationMutation(
    $externalCandidateId: ID!
    $certificationId: ID!
    $file: Upload!
  ) {
    uploadExternalCandidateCertification(
      externalCandidateId: $externalCandidateId
      certificationId: $certificationId
      file: $file
    )
  }
`;

export const AGENCY_ARCHIVE_BULLHORN_JOB_MUTATION = gql`
  mutation CloseJob($bullhornJobId: ID!) {
    archiveBullhornJob(bullhornJobId: $bullhornJobId)
  }
`;

export const RECRUITER_VIEWED_CANDIDATE_FOR_JOB_MUTATION = gql`
  mutation RecruiterViewedCandidateForJobMutation(
    $candidateProfileId: ID!
    $moblyzeJobId: ID!
    $recruiterViewType: RecruiterCandidateViewType!
  ) {
    recruiterViewedCandidateForJob(
      candidateProfileId: $candidateProfileId
      moblyzeJobId: $moblyzeJobId
      recruiterViewType: $recruiterViewType
    )
  }
`;

export const GET_CANDIDATES_QUERY = gql`
  query AgencyCandidates(
    $offset: Int
    $limit: Int
    $search: String
    $favourite: Boolean
    $roles: [String]
    $skills: [String]
  ) {
    agencyCandidates(offset: $offset, limit: $limit, search: $search, favourite: $favourite, roles: $roles, skills: $skills) {
      candidates {
        id
        candidateProfileId
        favourite
        fullName
        email
        roles
        skills
        hasResume
      }
      total
      limit
      offset
    }
  }
`;

export const AGENCY_TOGGLE_FAVOURITE_CANDIDATE_MUTATION = gql`
  mutation ClientToggleFavouriteCandidate(
    $candidateProfileId: ID!
  ) {
    clientToggleFavouriteCandidate(
      candidateProfileId: $candidateProfileId
    )
  }
`;

export const DELETE_RECRUITER_MUTATION = gql`
  mutation DeleteRecruiter($id: ID!) {
    deleteRecruiter(id: $id) {
      id
      fullName
      firstName
      lastName
      email
    }
  }
`;

// Owner-admin only. Requests deletion of a candidate's account on their behalf
// (same soft-delete + PII-anonymization pipeline as the candidate's own in-app
// deletion). Irreversible. Idempotent if a deletion was already requested.
export const ADMIN_REQUEST_CANDIDATE_ACCOUNT_DELETION_MUTATION = gql`
  mutation AdminRequestCandidateAccountDeletion(
    $candidateProfileId: ID!
    $reason: String!
  ) {
    adminRequestCandidateAccountDeletion(
      candidateProfileId: $candidateProfileId
      reason: $reason
    )
  }
`;

export const GET_PAGINATED_JOBS_QUERY = gql`
  query PaginatedAgencyJobs(
    $offset: Int
    $limit: Int
    $status: String
    $search: String
  ) {
    paginatedAgencyJobs(offset: $offset, limit: $limit, status: $status, search: $search) {
      jobs {
        id
        bullhornId
        title
        category
        issues
        department {
          id
          name
        }
        location
        status
        createdAt
      }
      total
      limit
      offset
    }
  }
`;

export const UPDATE_AGENCY_JOB_WEBSITE_AND_TRIGGER_SCRAPING = gql`
  mutation UpdateAgencyJobWebsiteAndTriggerScraping(
    $jobWebsite: String!
  ) {
    updateAgencyJobWebsiteAndTriggerScraping(
      jobWebsite: $jobWebsite
    ) {
      id
      slugId
      name
      logoUrl
      type
      jobWebsite
      createdAt
      updatedAt
    }
  }
`;